import Splide from '@splidejs/splide';

document.documentElement.classList.add('js-enabled');

const pointerInputClass = 'is-pointer-input';
const pointerStartEvent = 'PointerEvent' in window ? 'pointerdown' : 'touchstart';

document.addEventListener(
	pointerStartEvent,
	() => document.documentElement.classList.add(pointerInputClass),
	{ capture: true, passive: true },
);

document.addEventListener(
	'keydown',
	() => document.documentElement.classList.remove(pointerInputClass),
	{ capture: true },
);

const focusableSelector = [
	'a[href]',
	'button:not([disabled])',
	'iframe',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

const scrollRevealElements = document.querySelectorAll('[data-scroll-reveal]');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const revealScrollElement = (element) => {
	element.classList.add('is-scroll-revealed');
};

const getSamePageAnchorTarget = (href) => {
	if (!href) {
		return null;
	}

	const url = new URL(href, window.location.href);

	if (
		url.origin !== window.location.origin ||
		url.pathname !== window.location.pathname ||
		url.search !== window.location.search ||
		!url.hash ||
		url.hash === '#'
	) {
		return null;
	}

	try {
		return document.getElementById(decodeURIComponent(url.hash.slice(1)));
	} catch {
		return null;
	}
};

const prepareAnchorTarget = (target) => {
	if (!target?.matches('[data-scroll-reveal]')) {
		return;
	}

	target.style.setProperty('--scroll-reveal-delay', '0ms');
	target.classList.add('is-anchor-target');
	revealScrollElement(target);

	requestAnimationFrame(() => {
		target.classList.remove('is-anchor-target');
	});
};

document.addEventListener(
	'click',
	(event) => {
		const anchor = event.target.closest('a[href]');

		if (anchor) {
			prepareAnchorTarget(getSamePageAnchorTarget(anchor.href));
		}
	},
	{ capture: true },
);

prepareAnchorTarget(getSamePageAnchorTarget(window.location.href));

if (scrollRevealElements.length > 0) {
	if (reducedMotion.matches || !('IntersectionObserver' in window)) {
		scrollRevealElements.forEach(revealScrollElement);
	} else {
		const groupNextRevealTimes = new Map();
		const groupElementOrders = new Map();
		const pendingGroupElements = new Map();
		let groupRevealFrame = null;

		scrollRevealElements.forEach((element) => {
			const revealGroup = element.dataset.scrollRevealGroup;

			if (!revealGroup) {
				return;
			}

			if (!groupElementOrders.has(revealGroup)) {
				groupElementOrders.set(revealGroup, new Map());
			}

			const groupOrders = groupElementOrders.get(revealGroup);
			groupOrders.set(element, groupOrders.size);
		});

		const flushPendingGroupElements = () => {
			const currentTime = performance.now();

			pendingGroupElements.forEach((pendingElements, revealGroup) => {
				const groupOrders = groupElementOrders.get(revealGroup);
				const orderedElements = [...pendingElements].sort(
					(firstElement, secondElement) =>
						groupOrders.get(firstElement) - groupOrders.get(secondElement),
				);
				let nextRevealTime = Math.max(
					groupNextRevealTimes.get(revealGroup) || currentTime,
					currentTime,
				);

				orderedElements.forEach((element) => {
					element.style.setProperty(
						'--scroll-reveal-delay',
						`${nextRevealTime - currentTime}ms`,
					);
					revealScrollElement(element);
					nextRevealTime += 80;
				});

				groupNextRevealTimes.set(revealGroup, nextRevealTime);
			});

			pendingGroupElements.clear();
			groupRevealFrame = null;
		};

		scrollRevealElements.forEach((element) => {
			const scrollRevealObserver = new IntersectionObserver(
				([entry]) => {
					if (!entry?.isIntersecting) {
						return;
					}

					const revealGroup = element.dataset.scrollRevealGroup;

					if (revealGroup) {
						if (!pendingGroupElements.has(revealGroup)) {
							pendingGroupElements.set(revealGroup, new Set());
						}

						pendingGroupElements.get(revealGroup).add(element);

						if (groupRevealFrame === null) {
							groupRevealFrame = requestAnimationFrame(
								flushPendingGroupElements,
							);
						}
					} else {
						revealScrollElement(element);
					}

					scrollRevealObserver.disconnect();
				},
				{
					rootMargin: '0px 0px -12% 0px',
					threshold: 0,
				},
			);

			scrollRevealObserver.observe(element);
		});
	}
}

const heroLogo = document.querySelector('[data-hero-logo]');

if (heroLogo) {
	const revealLogo = () => {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => heroLogo.classList.add('is-ready'));
		});
	};

	if (heroLogo.complete) {
		revealLogo();
	} else {
		heroLogo.addEventListener('load', revealLogo, { once: true });
	}
}

const heroBackground = document.querySelector('[data-hero-background]');
const heroVideo = heroBackground?.querySelector('[data-hero-video]');
const heroVideoLoading = heroBackground?.querySelector('[data-hero-video-loading]');

if (heroBackground && heroVideo) {
	const heroVideoSource = heroVideo.dataset.heroVideoSrc;
	let heroVideoLoadingProgress = 0;
	let heroVideoLoadingTimer = null;
	let heroVideoLoadingFallbackTimer = null;

	const setHeroVideoLoadingProgress = (progress) => {
		if (!heroVideoLoading || heroVideoLoading.classList.contains('is-complete')) {
			return;
		}

		heroVideoLoadingProgress = Math.max(
			heroVideoLoadingProgress,
			Math.min(progress, 100),
		);
		heroVideoLoading.style.setProperty(
			'--hero-video-loading-progress',
			String(heroVideoLoadingProgress / 100),
		);
		heroVideoLoading.setAttribute(
			'aria-valuenow',
			String(Math.round(heroVideoLoadingProgress)),
		);
	};

	const completeHeroVideoLoading = () => {
		window.clearInterval(heroVideoLoadingTimer);
		window.clearTimeout(heroVideoLoadingFallbackTimer);
		setHeroVideoLoadingProgress(100);
		window.setTimeout(() => {
			heroVideoLoading?.classList.add('is-complete');
			heroVideoLoading?.removeAttribute('role');
			heroVideoLoading?.removeAttribute('aria-valuenow');
		}, 280);
	};

	if (heroVideoLoading) {
		setHeroVideoLoadingProgress(8);
		heroVideoLoadingTimer = window.setInterval(() => {
			const nextProgress =
				heroVideoLoadingProgress +
				Math.max(0.5, (88 - heroVideoLoadingProgress) * 0.045);
			setHeroVideoLoadingProgress(Math.min(nextProgress, 88));
		}, 250);
		heroVideoLoadingFallbackTimer = window.setTimeout(
			completeHeroVideoLoading,
			15000,
		);
	}

	if (window.Vimeo?.Player && heroVideoSource) {
		const heroVideoMobileBreakpoint = window.matchMedia('(max-width: 720px)');
		const heroVideoUrl = new URL(heroVideoSource, window.location.href);
		// Let Vimeo adapt quality to the viewer's network, device, and player size.
		heroVideoUrl.searchParams.set('initial_quality', 'auto');
		heroVideoUrl.searchParams.set('max_quality', 'auto');

		const updateHeroVideoCover = () => {
			if (!heroVideoMobileBreakpoint.matches) {
				heroVideo.style.removeProperty('--hero-video-scale');
				return;
			}

			const playerWidth = Math.max(heroBackground.clientWidth, 1);
			const playerHeight = playerWidth * (9 / 16);
			const coverScale = Math.max(
				heroBackground.clientHeight / playerHeight,
				1,
			);

			heroVideo.style.setProperty('--hero-video-scale', String(coverScale));
		};

		let heroVideoCoverFrame = null;
		const requestHeroVideoCoverUpdate = () => {
			window.cancelAnimationFrame(heroVideoCoverFrame);
			heroVideoCoverFrame = window.requestAnimationFrame(updateHeroVideoCover);
		};

		requestHeroVideoCoverUpdate();
		heroVideoMobileBreakpoint.addEventListener(
			'change',
			requestHeroVideoCoverUpdate,
		);
		window.addEventListener('resize', requestHeroVideoCoverUpdate);
		window.addEventListener('orientationchange', requestHeroVideoCoverUpdate);
		window.visualViewport?.addEventListener(
			'resize',
			requestHeroVideoCoverUpdate,
		);

		if ('ResizeObserver' in window) {
			const heroBackgroundResizeObserver = new ResizeObserver(
				requestHeroVideoCoverUpdate,
			);
			heroBackgroundResizeObserver.observe(heroBackground);
		}
		heroVideo.src = heroVideoUrl.toString();

		const player = new window.Vimeo.Player(heroVideo);
		const disableHeroTextTrack = () => player.disableTextTrack().catch(() => {});
		const videoRegions = [
			document.querySelector('.sec-hero'),
			document.querySelector('.sec-schedule'),
		].filter(Boolean);
		const regionVisibility = new Map(videoRegions.map((region) => [region, false]));
		let playerReady = false;
		let requestedPlaybackState = null;
		let resumePlaybackTimer = null;

		const revealHeroVideo = async () => {
			await disableHeroTextTrack();
			heroBackground.classList.add('is-ready');
		};

		const shouldHeroVideoPlay = () =>
			document.visibilityState === 'visible' &&
			[...regionVisibility.values()].some(Boolean);

		const syncHeroVideoPlayback = ({ force = false } = {}) => {
			const shouldPlay = shouldHeroVideoPlay();

			heroBackground.classList.toggle('is-active', shouldPlay);

			if (
				!playerReady ||
				(!force && requestedPlaybackState === shouldPlay)
			) {
				return;
			}

			requestedPlaybackState = shouldPlay;
			const playbackCommand = shouldPlay ? player.play() : player.pause();
			playbackCommand.catch(() => {
				if (requestedPlaybackState === shouldPlay) {
					requestedPlaybackState = null;
				}
			});
		};

		const resumeHeroVideoPlayback = () => {
			if (!shouldHeroVideoPlay()) {
				return;
			}

			// iOS can suspend the Vimeo iframe without updating our cached playback
			// state. Force a new play request now and once more after the iframe wakes.
			syncHeroVideoPlayback({ force: true });
			window.clearTimeout(resumePlaybackTimer);
			resumePlaybackTimer = window.setTimeout(() => {
				if (shouldHeroVideoPlay()) {
					syncHeroVideoPlayback({ force: true });
				}
			}, 300);
		};

		player.on('playing', () => {
			requestedPlaybackState = true;
			completeHeroVideoLoading();
			revealHeroVideo();
		});
		player.on('progress', ({ percent }) => {
			if (Number.isFinite(percent)) {
				setHeroVideoLoadingProgress(20 + percent * 70);
			}
		});
		player.on('pause', () => {
			requestedPlaybackState = false;
			if (shouldHeroVideoPlay()) {
				resumeHeroVideoPlayback();
			}
		});

		if ('IntersectionObserver' in window) {
			const videoRegionObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						regionVisibility.set(entry.target, entry.isIntersecting);
					});
					syncHeroVideoPlayback();
				},
				{
					threshold: 0,
				},
			);

			videoRegions.forEach((region) => videoRegionObserver.observe(region));
		} else {
			videoRegions.forEach((region) => regionVisibility.set(region, true));
		}

		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				resumeHeroVideoPlayback();
			} else {
				window.clearTimeout(resumePlaybackTimer);
				syncHeroVideoPlayback();
			}
		});
		window.addEventListener('pageshow', resumeHeroVideoPlayback);
		window.addEventListener('focus', resumeHeroVideoPlayback);

		player
			.ready()
			.then(async () => {
				setHeroVideoLoadingProgress(35);
				await Promise.allSettled([
					disableHeroTextTrack(),
					player.setLoop(true),
					player.setMuted(true),
					player.setVolume(0),
				]);
				playerReady = true;
				syncHeroVideoPlayback();
			})
			.catch(completeHeroVideoLoading);
	} else {
		completeHeroVideoLoading();
	}
}

const menuToggle = document.querySelector('[data-menu-toggle]');
const menuPanel = document.querySelector('[data-menu-panel]');
let menuLastFocus = null;

const setMenuOpen = (shouldOpen) => {
	if (!menuToggle || !menuPanel) {
		return;
	}

	document.body.classList.toggle('menu-open', shouldOpen);
	menuToggle.setAttribute('aria-expanded', String(shouldOpen));
	menuPanel.setAttribute('aria-hidden', String(!shouldOpen));
	menuPanel.inert = !shouldOpen;

	if (shouldOpen) {
		menuLastFocus = document.activeElement;
		window.requestAnimationFrame(() => {
			menuPanel.querySelector('a')?.focus();
		});
	} else if (menuLastFocus instanceof HTMLElement) {
		menuLastFocus.focus();
	}
};

menuToggle?.addEventListener('click', () => {
	setMenuOpen(menuToggle.getAttribute('aria-expanded') !== 'true');
});

menuPanel?.querySelectorAll('a').forEach((link) => {
	link.addEventListener('click', () => setMenuOpen(false));
});

const ticketAnchor = document.querySelector('[data-ticket-anchor]');

ticketAnchor?.addEventListener('click', (event) => {
	const targetId = ticketAnchor.getAttribute('href');
	const target = targetId ? document.querySelector(targetId) : null;

	if (!target) {
		return;
	}

	event.preventDefault();
	window.history.pushState(null, '', targetId);
	target.scrollIntoView({
		behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
			? 'auto'
			: 'smooth',
		block: 'start',
	});
});

document.addEventListener('keydown', (event) => {
	if (!document.body.classList.contains('menu-open') || !menuPanel) {
		return;
	}

	if (event.key === 'Escape') {
		event.preventDefault();
		setMenuOpen(false);
		return;
	}

	if (event.key !== 'Tab') {
		return;
	}

	const focusable = [menuToggle, ...menuPanel.querySelectorAll(focusableSelector)].filter(
		(element) => element instanceof HTMLElement && !element.hidden,
	);
	const first = focusable[0];
	const last = focusable[focusable.length - 1];

	if (event.shiftKey && document.activeElement === first) {
		event.preventDefault();
		last?.focus();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first?.focus();
	}
});

const singleTouchFlickBreakpoint = window.matchMedia('(max-width: 720px)');
const singleTouchFlickThreshold = 30;
const singleTouchDirectionThreshold = 8;

const mountSingleTouchFlickSlider = (slider, options) => {
	const splide = new Splide(slider, options);
	let touchStart = null;
	let suppressClick = false;
	let suppressClickTimer = null;

	slider.addEventListener(
		'touchstart',
		(event) => {
			if (!singleTouchFlickBreakpoint.matches || event.touches.length !== 1) {
				touchStart = null;
				return;
			}

			const touch = event.touches[0];
			touchStart = { x: touch.clientX, y: touch.clientY, direction: null };
		},
		{ passive: true },
	);

	slider.addEventListener(
		'touchmove',
		(event) => {
			if (!touchStart || !singleTouchFlickBreakpoint.matches || event.touches.length !== 1) {
				return;
			}

			const touch = event.touches[0];
			const deltaX = touch.clientX - touchStart.x;
			const deltaY = touch.clientY - touchStart.y;

			if (
				!touchStart.direction &&
				Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= singleTouchDirectionThreshold
			) {
				touchStart.direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
			}

			if (touchStart.direction === 'horizontal' && event.cancelable) {
				event.preventDefault();
			}
		},
		{ passive: false },
	);

	slider.addEventListener(
		'touchend',
		(event) => {
			if (!touchStart || !singleTouchFlickBreakpoint.matches) {
				touchStart = null;
				return;
			}

			const touch = event.changedTouches[0];
			const direction = touchStart.direction;
			const deltaX = touchStart.x - touch.clientX;
			const deltaY = touchStart.y - touch.clientY;
			touchStart = null;

			if (
				direction === 'vertical' ||
				Math.abs(deltaX) < singleTouchFlickThreshold ||
				Math.abs(deltaX) <= Math.abs(deltaY)
			) {
				return;
			}

			suppressClick = true;
			window.clearTimeout(suppressClickTimer);
			suppressClickTimer = window.setTimeout(() => {
				suppressClick = false;
			}, 400);
			splide.go(deltaX > 0 ? '>' : '<');
		},
		{ passive: true },
	);

	slider.addEventListener(
		'touchcancel',
		() => {
			touchStart = null;
		},
		{ passive: true },
	);

	slider.addEventListener(
		'click',
		(event) => {
			if (!suppressClick) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			suppressClick = false;
			window.clearTimeout(suppressClickTimer);
		},
		{ capture: true },
	);

	splide.mount();
};

document.querySelectorAll('[data-comment-slider]').forEach((slider) => {
	mountSingleTouchFlickSlider(slider, {
		type: 'loop',
		focus: 'center',
		perPage: 1,
		fixedWidth: 'min(72vw, 980px)',
		gap: '40px',
		arrows: true,
		pagination: true,
		speed: 700,
		drag: true,
		keyboard: 'focused',
		breakpoints: {
			720: {
				fixedWidth: '86vw',
				gap: '16px',
				speed: 550,
				drag: false,
			},
		},
	});
});

document.querySelectorAll('[data-content-slider]').forEach((slider) => {
	mountSingleTouchFlickSlider(slider, {
		type: 'loop',
		focus: 'center',
		perPage: 1,
		fixedWidth: '50.25rem',
		gap: '2.125rem',
		arrows: true,
		pagination: true,
		speed: 700,
		drag: true,
		keyboard: 'focused',
		updateOnMove: true,
		breakpoints: {
			720: {
				fixedWidth: '20rem',
				gap: '1rem',
				speed: 550,
				drag: false,
			},
		},
	});
});

const videoModal = document.querySelector('[data-video-modal]');
const videoDialog = videoModal?.querySelector('.video-modal__dialog');
const videoPlayer = videoModal?.querySelector('[data-video-player]');
const videoCloseButton = videoModal?.querySelector('.video-modal__close');
let videoLastFocus = null;

const updateVideoModalLayout = () => {
	if (
		!videoModal ||
		!videoDialog ||
		!videoPlayer ||
		!videoCloseButton ||
		videoModal.hidden
	) {
		return;
	}

	const modalStyle = window.getComputedStyle(videoModal);
	const dialogStyle = window.getComputedStyle(videoDialog);
	const horizontalPadding =
		Number.parseFloat(modalStyle.paddingLeft) +
		Number.parseFloat(modalStyle.paddingRight);
	const verticalPadding =
		Number.parseFloat(modalStyle.paddingTop) +
		Number.parseFloat(modalStyle.paddingBottom);
	const dialogGap = Number.parseFloat(dialogStyle.rowGap) || 0;
	const availableWidth = Math.max(
		Math.min(videoModal.clientWidth - horizontalPadding, 1200),
		1,
	);
	const availableHeight = Math.max(
		videoModal.clientHeight -
			verticalPadding -
			2 *
				(videoCloseButton.getBoundingClientRect().height +
					dialogGap),
		1,
	);
	const playerWidth = Math.min(availableWidth, availableHeight * (16 / 9));

	videoModal.style.setProperty('--video-modal-player-width', `${playerWidth}px`);
};

const closeVideoModal = () => {
	if (!videoModal || !videoPlayer) {
		return;
	}

	videoModal.hidden = true;
	videoModal.style.removeProperty('--video-modal-player-width');
	videoPlayer.replaceChildren();
	document.body.classList.remove('video-modal-open');

	if (videoLastFocus instanceof HTMLElement) {
		videoLastFocus.focus();
	}
};

const openVideoModal = (button) => {
	if (!videoModal || !videoPlayer) {
		return;
	}

	const platform = button.dataset.videoPlatform === 'youtube' ? 'youtube' : 'vimeo';
	const rawVideoId = button.dataset.videoId || '';
	const videoId =
		platform === 'youtube'
			? rawVideoId.replace(/[^a-zA-Z0-9_-]/g, '')
			: rawVideoId.replace(/[^0-9]/g, '');

	if (!videoId) {
		return;
	}

	const iframe = document.createElement('iframe');
	iframe.title = 'コメント動画';
	iframe.allow = 'autoplay; fullscreen; picture-in-picture';
	iframe.allowFullscreen = true;
	iframe.referrerPolicy = 'strict-origin-when-cross-origin';
	iframe.src =
		platform === 'youtube'
			? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0`
			: `https://player.vimeo.com/video/${encodeURIComponent(videoId)}?autoplay=1&title=0&byline=0&portrait=0`;

	videoLastFocus = button;
	videoPlayer.replaceChildren(iframe);
	videoModal.hidden = false;
	document.body.classList.add('video-modal-open');
	updateVideoModalLayout();
	window.requestAnimationFrame(() => {
		updateVideoModalLayout();
		videoCloseButton?.focus();
	});
};

window.addEventListener('resize', updateVideoModalLayout);
window.visualViewport?.addEventListener('resize', updateVideoModalLayout);

document.querySelectorAll('[data-video-open]').forEach((button) => {
	button.addEventListener('click', () => openVideoModal(button));
});

videoModal?.querySelectorAll('[data-video-close]').forEach((button) => {
	button.addEventListener('click', closeVideoModal);
});

videoModal?.addEventListener('keydown', (event) => {
	if (event.key === 'Escape') {
		event.preventDefault();
		closeVideoModal();
		return;
	}

	if (event.key !== 'Tab') {
		return;
	}

	const focusable = [...videoModal.querySelectorAll(focusableSelector)].filter(
		(element) => element instanceof HTMLElement && !element.hidden,
	);
	const first = focusable[0];
	const last = focusable[focusable.length - 1];

	if (event.shiftKey && document.activeElement === first) {
		event.preventDefault();
		last?.focus();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first?.focus();
	}
});

const galleryModal = document.querySelector('[data-gallery-modal]');
const gallerySliderElement = galleryModal?.querySelector('[data-gallery-slider]');
const galleryProgress = galleryModal?.querySelector('[data-gallery-progress]');
const galleryCaption = galleryModal?.querySelector('[data-gallery-caption-output]');
const galleryCloseButton = galleryModal?.querySelector('.gallery-modal__close');
let gallerySlider = null;
let galleryLastFocus = null;

const updateGalleryProgress = (index, slideCount) => {
	if (!galleryProgress) {
		return;
	}

	galleryProgress.style.setProperty(
		'--gallery-progress-ratio',
		String((Math.max(index, 0) + 1) / Math.max(slideCount, 1)),
	);
	galleryProgress.setAttribute('aria-valuemax', String(Math.max(slideCount, 1)));
	galleryProgress.setAttribute('aria-valuenow', String(Math.max(index, 0) + 1));

	if (galleryCaption && gallerySliderElement) {
		const slides = gallerySliderElement.querySelectorAll(
			'.splide__slide:not(.splide__slide--clone)',
		);
		const caption = slides[index]?.dataset.galleryCaption || '';

		galleryCaption.innerHTML = caption;
		galleryCaption.hidden = !caption;
	}
};

const updateGalleryLayout = () => {
	if (
		!galleryModal ||
		!gallerySliderElement ||
		galleryModal.hidden
	) {
		return;
	}

	const modalStyle = window.getComputedStyle(galleryModal);
	const availableHeight =
		galleryModal.clientHeight -
		Number.parseFloat(modalStyle.paddingTop) -
		Number.parseFloat(modalStyle.paddingBottom);
	const progressStyle = galleryProgress ? window.getComputedStyle(galleryProgress) : null;
	const progressSpace = galleryProgress
		? galleryProgress.getBoundingClientRect().height +
			Number.parseFloat(progressStyle.marginTop) +
			Number.parseFloat(progressStyle.marginBottom)
		: 0;
	const captionStyle =
		galleryCaption && !galleryCaption.hidden ? window.getComputedStyle(galleryCaption) : null;
	const captionSpace = captionStyle
		? galleryCaption.getBoundingClientRect().height +
			Number.parseFloat(captionStyle.marginTop) +
			Number.parseFloat(captionStyle.marginBottom)
		: 0;
	const controlsSpace = progressSpace + captionSpace;
	const contentHeight = Math.max(availableHeight - controlsSpace, 1);

	gallerySliderElement.style.setProperty('--gallery-slider-height', `${availableHeight}px`);
	gallerySliderElement.style.setProperty('--gallery-controls-space', `${controlsSpace}px`);
	gallerySliderElement.style.setProperty(
		'--gallery-content-max-height',
		`${contentHeight}px`,
	);

	gallerySliderElement.querySelectorAll('.gallery-modal__figure').forEach((figure) => {
		figure.style.setProperty('--gallery-media-max-height', `${contentHeight}px`);
	});
};

const closeGalleryModal = () => {
	if (!galleryModal) {
		return;
	}

	gallerySlider?.destroy(true);
	gallerySlider = null;
	galleryModal.hidden = true;
	document.body.classList.remove('gallery-modal-open');

	if (galleryLastFocus instanceof HTMLElement) {
		galleryLastFocus.focus();
	}
};

const openGalleryModal = (button) => {
	if (!galleryModal || !gallerySliderElement) {
		return;
	}

	const requestedIndex = Number.parseInt(button.dataset.galleryIndex || '0', 10);
	const slideCount = gallerySliderElement.querySelectorAll('.splide__slide').length;
	const initialIndex = Number.isFinite(requestedIndex)
		? Math.min(Math.max(requestedIndex, 0), Math.max(slideCount - 1, 0))
		: 0;

	galleryLastFocus = button;
	galleryModal.hidden = false;
	document.body.classList.add('gallery-modal-open');

	window.requestAnimationFrame(() => {
		gallerySlider = new Splide(gallerySliderElement, {
			type: slideCount > 1 ? 'fade' : 'slide',
			rewind: slideCount > 1,
			rewindByDrag: slideCount > 1,
			start: initialIndex,
			perPage: 1,
			arrows: slideCount > 1,
			pagination: false,
			speed: 650,
			drag: slideCount > 1,
			keyboard: 'focused',
			updateOnMove: true,
		});
		gallerySlider.on('mounted', () => {
			updateGalleryProgress(gallerySlider.index, slideCount);
		});
		gallerySlider.on('move', (newIndex) => {
			updateGalleryProgress(newIndex, slideCount);
			window.requestAnimationFrame(updateGalleryLayout);
		});
		gallerySlider.mount();
		updateGalleryLayout();
		window.requestAnimationFrame(updateGalleryLayout);

		galleryCloseButton?.focus();
	});
};

window.addEventListener('resize', updateGalleryLayout);
window.visualViewport?.addEventListener('resize', updateGalleryLayout);

document.querySelectorAll('[data-gallery-open]').forEach((button) => {
	button.addEventListener('click', () => openGalleryModal(button));
});

galleryModal?.querySelectorAll('[data-gallery-close]').forEach((button) => {
	button.addEventListener('click', closeGalleryModal);
});

galleryModal?.addEventListener('keydown', (event) => {
	if (event.key === 'Escape') {
		event.preventDefault();
		closeGalleryModal();
		return;
	}

	if (event.key !== 'Tab') {
		return;
	}

	const focusable = [...galleryModal.querySelectorAll(focusableSelector)].filter(
		(element) => element instanceof HTMLElement && !element.hidden,
	);
	const first = focusable[0];
	const last = focusable[focusable.length - 1];

	if (event.shiftKey && document.activeElement === first) {
		event.preventDefault();
		last?.focus();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first?.focus();
	}
});

document.querySelectorAll('[data-news-toggle]').forEach((button) => {
	const panelId = button.getAttribute('aria-controls');
	const panel = panelId ? document.getElementById(panelId) : null;

	if (!panel) {
		return;
	}

	panel.hidden = false;
	panel.dataset.open = 'false';
	panel.setAttribute('aria-hidden', 'true');
	panel.inert = true;

	button.addEventListener('click', () => {
		const shouldOpen = button.getAttribute('aria-expanded') !== 'true';

		button.setAttribute('aria-expanded', String(shouldOpen));
		panel.dataset.open = String(shouldOpen);
		panel.setAttribute('aria-hidden', String(!shouldOpen));
		panel.inert = !shouldOpen;
	});
});
